import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ChatText, Gavel, Folder, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const CASE_TONE = { offen: "amber", geschlossen: "green" };
const ORD_TONE = { erstellt: "gray", klassifiziert: "blue", abgeschlossen: "green" };

function CaseDetail({ cid, assets, onChange }) {
  const [data, setData] = useState(null);
  const [comm, setComm] = useState({ channel: "telefon", direction: "eingehend", text: "" });
  const [dec, setDec] = useState("");
  const load = useCallback(() => api.get(`/service/cases/${cid}`).then((r) => setData(r.data)), [cid]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Skeleton className="h-40" />;
  const addComm = async () => { await api.post(`/service/cases/${cid}/communication`, comm); setComm({ ...comm, text: "" }); load(); onChange?.(); toast.success("Kommunikation erfasst"); };
  const addDec = async () => { await api.post(`/service/cases/${cid}/decision`, { text: dec }); setDec(""); load(); toast.success("Entscheidung erfasst"); };
  const close = async () => { await api.post(`/service/cases/${cid}/close`); load(); onChange?.(); toast.success("Fallakte geschlossen"); };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><StatusBadge tone={CASE_TONE[data.status]}>{data.status}</StatusBadge><span className="font-mono text-xs text-primary">{data.case_number}</span></div>
      <p className="text-sm text-muted-foreground">{data.description}</p>
      <div><Label2><ChatText size={13} className="mr-1 inline" />Kommunikationshistorie</Label2>
        <div className="mt-2 space-y-2">
          {data.communications.map((c) => (
            <div key={c.id} className="rounded-md border border-border p-2 text-sm">
              <span className="font-semibold">{c.channel} · {c.direction}</span> — {c.text}
              <div className="text-xs text-muted-foreground">{new Date(c.ts).toLocaleString("de-DE")} · {c.actor}</div>
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <Select value={comm.channel} onValueChange={(v) => setComm((s) => ({ ...s, channel: v }))}>
            <SelectTrigger className="sm:w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{["telefon", "email", "portal", "vor_ort", "intern"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="case-comm-text" placeholder="Notiz…" value={comm.text} onChange={(e) => setComm((s) => ({ ...s, text: e.target.value }))} />
          <Button data-testid="case-comm-add" onClick={addComm} disabled={!comm.text}>Erfassen</Button>
        </div>
      </div>
      <div><Label2><Gavel size={13} className="mr-1 inline" />Entscheidungen</Label2>
        <div className="mt-2 space-y-1">{data.decisions.map((d) => <div key={d.id} className="text-sm">› {d.text} <span className="text-xs text-muted-foreground">({d.actor})</span></div>)}</div>
        <div className="mt-2 flex gap-2"><Input placeholder="Entscheidung…" value={dec} onChange={(e) => setDec(e.target.value)} /><Button onClick={addDec} disabled={!dec}>+</Button></div>
      </div>
      {data.status !== "geschlossen" && <Button data-testid="case-close" variant="outline" onClick={close}>Fallakte schließen</Button>}
    </div>
  );
}

function CasesTab() {
  const [cases, setCases] = useState(null);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ title: "", customer: "", description: "", asset_id: "" });
  const load = useCallback(() => { api.get("/service/cases").then((r) => setCases(r.data)); api.get("/assets").then((r) => setAssets(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    try { await api.post("/service/cases", { ...form, asset_id: form.asset_id || null }); toast.success("Fallakte angelegt"); setOpen(false); setForm({ title: "", customer: "", description: "", asset_id: "" }); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };
  if (!cases) return <Skeleton className="h-40" />;
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-case-button" className="gap-2"><Plus size={18} weight="bold" />Neue Fallakte</Button></DialogTrigger>
          <DialogContent data-testid="case-dialog"><DialogHeader><DialogTitle className="font-heading">Fallakte anlegen</DialogTitle></DialogHeader>
            <DialogDescription>Service Case für einen Kundenvorgang.</DialogDescription>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label2>Titel *</Label2><Input data-testid="case-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Kunde</Label2><Input value={form.customer} onChange={(e) => setForm((f) => ({ ...f, customer: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Anlage</Label2>
                <Select value={form.asset_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Keine</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label2>Beschreibung</Label2><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button data-testid="case-save" onClick={submit} disabled={!form.title}>Anlegen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="divide-y divide-border rounded-md border">
        {cases.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Fallakten.</div>}
        {cases.map((c) => (
          <div key={c.id} data-testid="case-row" onClick={() => setDetail(c.id)} className="flex cursor-pointer items-center justify-between px-5 py-4 hover:bg-accent">
            <div><div className="font-semibold">{c.title}</div><div className="text-xs text-muted-foreground">{c.case_number} · {c.customer || "—"}</div></div>
            <StatusBadge tone={CASE_TONE[c.status]}>{c.status}</StatusBadge>
          </div>
        ))}
      </Card>
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="case-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading">Fallakte</DialogTitle></DialogHeader>
          <DialogDescription>Timeline, Kommunikation und Entscheidungen.</DialogDescription>
          {detail && <CaseDetail cid={detail} assets={assets} onChange={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", asset_id: "" });
  const load = useCallback(() => { api.get("/service/orders").then((r) => setOrders(r.data)); api.get("/assets").then((r) => setAssets(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => { await api.post("/service/orders", { ...form, asset_id: form.asset_id || null }); toast.success("Serviceauftrag erstellt"); setOpen(false); setForm({ title: "", description: "", asset_id: "" }); load(); };
  const classify = async (id) => { await api.post(`/service/orders/${id}/classify`, { classification: "störung", priority: "mittel" }); load(); toast.success("Klassifiziert"); };
  const complete = async (id) => { await api.post(`/service/orders/${id}/complete`, { result: "erledigt" }); load(); toast.success("Abgeschlossen"); };
  if (!orders) return <Skeleton className="h-40" />;
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-service-order-button" className="gap-2"><Plus size={18} weight="bold" />Neuer Serviceauftrag</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle className="font-heading">Serviceauftrag</DialogTitle></DialogHeader>
            <DialogDescription>Auftrag aus einer Serviceanfrage.</DialogDescription>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label2>Titel *</Label2><Input data-testid="service-order-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Anlage</Label2>
                <Select value={form.asset_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Keine</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label2>Beschreibung</Label2><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button data-testid="service-order-save" onClick={submit} disabled={!form.title}>Erstellen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="divide-y divide-border rounded-md border">
        {orders.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Keine Serviceaufträge.</div>}
        {orders.map((o) => (
          <div key={o.id} data-testid="service-order-row" className="flex items-center justify-between gap-3 px-5 py-4">
            <div><div className="font-semibold">{o.title}</div><div className="text-xs text-muted-foreground">{o.asset_name || "—"}</div></div>
            <div className="flex items-center gap-2">
              <StatusBadge tone={ORD_TONE[o.status]}>{o.status}</StatusBadge>
              {o.status === "erstellt" && <Button size="sm" variant="outline" onClick={() => classify(o.id)}>Klassifizieren</Button>}
              {o.status !== "abgeschlossen" && <Button size="sm" onClick={() => complete(o.id)}>Abschließen</Button>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export default function Service() {
  return (
    <div>
      <PageHeader title="Service" subtitle="Fallakten (Service Case) & Serviceaufträge" />
      <Tabs defaultValue="cases">
        <TabsList data-testid="service-tabs">
          <TabsTrigger value="cases" className="gap-1.5"><Folder size={16} />Fallakten</TabsTrigger>
          <TabsTrigger value="orders" className="gap-1.5"><ClockCounterClockwise size={16} />Serviceaufträge</TabsTrigger>
        </TabsList>
        <TabsContent value="cases"><CasesTab /></TabsContent>
        <TabsContent value="orders"><OrdersTab /></TabsContent>
      </Tabs>
    </div>
  );
}
